import { useState } from 'react';
import { Metric, Table, cn, type Column } from '@evogard/ui';
import type { ManagementPeripherals } from '@evogard/contract';
import {
  PERIPHERAL_PANEL_TOP_PROVIDERS,
  PERIPHERAL_PART_ORIGINS,
  count,
  format,
  fromColumn,
  subtract,
  sum,
  toColumn,
  ZERO,
  type ClaimFlowPeriod,
  type Money,
} from '@evogard/domain';
import { PanelCard } from './PanelCard.js';
import type { useManagementPeripherals } from './useManagement.js';

/**
 * O dinheiro do Painel da Gestão dos periféricos (28/10/2026): o total
 * aprovado e a divisão pela cobertura, e as cotações aprovadas contra a menor
 * de cada item. Os valores chegam da API em texto decimal e viram `Money`
 * aqui — a soma de "outros" é em centavos, nunca em `number` de reais.
 */

type Data = ManagementPeripherals;
type Query = ReturnType<typeof useManagementPeripherals>;

const money = (text: string): Money => fromColumn(text)!;
const brl = (text: string) => format(money(text));
const ratio = (part: Money, whole: Money) => (whole > 0 ? part / whole : 0);
const share = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);

/* ------------------------------------------------------------------------ */

type Coverage = Data['money']['byCoverage'][number];

/**
 * O valor aprovado e a divisão. A barra de cima é o total, com a parte da
 * associação e a dos associados (a PI); embaixo, o mesmo por cobertura. A
 * conta é a da mesa, acionamento a acionamento — e quem ainda não tem valor
 * é contado à parte, não somado como zero.
 */
export function PeripheralMoneyChart({
  query,
  days,
}: {
  query: Query;
  days: ClaimFlowPeriod;
}) {
  const [asTable, setAsTable] = useState(false);
  const data = query.data?.money;
  const total = data ? money(data.totalApproved) : ZERO;
  const association = data ? money(data.associationShare) : ZERO;
  const maxCoverage = data
    ? Math.max(1, ...data.byCoverage.map((c) => money(c.totalApproved)))
    : 1;

  return (
    <PanelCard
      title="Valor aprovado e divisão"
      descricao="O total das cotações aprovadas, e quanto é da associação e quanto é do associado, pela cobertura de cada acionamento."
      query={query}
      empty={
        data && data.priced === 0
          ? data.pending > 0
            ? `Nenhum acionamento aberto nos últimos ${days} dias tem valor ainda: ${count(data.pending, 'está', 'estão')} sem item, sem cobertura ou com item sem cotação aprovada.`
            : `Nenhum acionamento aberto nos últimos ${days} dias.`
          : null
      }
      asTable={asTable}
      onToggle={() => setAsTable((v) => !v)}
      caption={
        data && (
          <>
            {count(data.priced, 'acionamento tem', 'acionamentos têm')} valor.
            {data.pending > 0 &&
              ` ${count(data.pending, 'ainda não tem', 'ainda não têm')}: sem item, sem cobertura ou com item sem cotação aprovada.`}
          </>
        )
      }
    >
      {data &&
        (asTable ? (
          <Table
            columns={
              [
                { title: 'Cobertura', cell: (r) => `${r.percent}%`, alignment: 'inicio' },
                { title: 'Acionamentos', cell: (r) => r.peripherals, alignment: 'fim' },
                {
                  title: 'Aprovado',
                  cell: (r) => brl(r.totalApproved),
                  alignment: 'fim',
                },
                {
                  title: 'Associação',
                  cell: (r) => brl(r.associationShare),
                  alignment: 'fim',
                },
                {
                  title: 'Associados',
                  cell: (r) =>
                    format(subtract(money(r.totalApproved), money(r.associationShare))),
                  alignment: 'fim',
                },
              ] satisfies Column<Coverage>[]
            }
            items={data.byCoverage}
            rowKey={(r) => String(r.percent)}
            label={`Valor aprovado por cobertura nos últimos ${days} dias`}
            empty="Nenhum acionamento com valor."
          />
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Metric label="Total aprovado" value={brl(data.totalApproved)} />
              <div
                className="flex h-3 overflow-hidden rounded-pilula bg-superficie-suave"
                role="img"
                aria-label={`Associação ${brl(data.associationShare)}, associados ${brl(data.memberShare)}.`}
              >
                <span
                  className="h-full bg-primaria"
                  style={{ width: `${ratio(association, total) * 100}%` }}
                />
                <span className="h-full flex-1 bg-fase-azul" />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-texto-suave">
                <span className="flex items-center gap-1.5">
                  <i aria-hidden className="size-2.5 rounded-[3px] bg-primaria" />
                  associação{' '}
                  <b className="font-semibold text-texto tabular-nums">
                    {brl(data.associationShare)}
                  </b>
                </span>
                <span className="flex items-center gap-1.5">
                  <i aria-hidden className="size-2.5 rounded-[3px] bg-fase-azul" />
                  associados (PI){' '}
                  <b className="font-semibold text-texto tabular-nums">
                    {brl(data.memberShare)}
                  </b>
                </span>
              </div>
            </div>
            <ol className="flex flex-col gap-2" aria-label="Valor aprovado por cobertura">
              {data.byCoverage.map((c) => (
                <li
                  key={c.percent}
                  className="grid grid-cols-[minmax(0,7.5rem)_minmax(0,1fr)_minmax(0,7.5rem)] items-center gap-3 text-xs"
                >
                  <span className="text-texto-suave">
                    Cobertura {c.percent}%{' '}
                    <span className="tabular-nums">({c.peripherals})</span>
                  </span>
                  <span className="flex h-2.5 overflow-hidden rounded-pilula bg-superficie-suave">
                    <span
                      className="h-full bg-primaria"
                      style={{
                        width: `${(money(c.associationShare) / maxCoverage) * 100}%`,
                      }}
                    />
                    <span
                      className="h-full bg-fase-azul"
                      style={{
                        width: `${((money(c.totalApproved) - money(c.associationShare)) / maxCoverage) * 100}%`,
                      }}
                    />
                  </span>
                  <span className="text-right font-semibold tabular-nums text-texto">
                    {brl(c.totalApproved)}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        ))}
    </PanelCard>
  );
}

/* ------------------------------------------------------------------------ */

type Provider = Data['quotes']['providers'][number];
type Origin = Data['quotes']['origins'][number];

const originLabel = (o: Origin['origin']) =>
  o === 'unknown' ? 'Sem origem informada' : PERIPHERAL_PART_ORIGINS[o];
const providerLabel = (p: Provider) => p.name ?? 'Sem fornecedor informado';

/** Os maiores fornecedores, e o resto somado numa linha só — em centavos. */
function topProviders(providers: readonly Provider[]): Provider[] {
  const top = providers.slice(0, PERIPHERAL_PANEL_TOP_PROVIDERS);
  const rest = providers.slice(PERIPHERAL_PANEL_TOP_PROVIDERS);
  if (rest.length === 0) return top;
  return [
    ...top,
    {
      name: `Outros ${rest.length} fornecedores`,
      items: rest.reduce((n, p) => n + p.items, 0),
      total: toColumn(sum(...rest.map((p) => money(p.total)))),
    },
  ];
}

/**
 * As cotações: a aprovada contra a menor não reprovada de cada item — quanto
 * se aprovou acima do mais barato —, a origem da peça e quem vendeu. Só o item
 * com cotação aprovada entra: o que ainda está sendo cotado não tem preço final.
 */
export function PeripheralQuotesChart({
  query,
  days,
}: {
  query: Query;
  days: ClaimFlowPeriod;
}) {
  const [asTable, setAsTable] = useState(false);
  const data = query.data?.quotes;
  const providers = data ? topProviders(data.providers) : [];
  const above = data ? money(data.aboveLowest) : ZERO;

  return (
    <PanelCard
      title="Cotações"
      descricao="A cotação aprovada de cada item contra a menor cotação dele, a origem da peça e os fornecedores."
      query={query}
      empty={
        data?.approvedItems === 0
          ? `Nenhum item dos acionamentos abertos nos últimos ${days} dias tem cotação aprovada ainda.`
          : null
      }
      asTable={asTable}
      onToggle={() => setAsTable((v) => !v)}
    >
      {data &&
        (asTable ? (
          <div className="flex flex-col gap-4">
            <Table
              columns={
                [
                  {
                    title: 'Origem da peça',
                    cell: (r) => originLabel(r.origin),
                    alignment: 'inicio',
                  },
                  { title: 'Itens', cell: (r) => r.items, alignment: 'fim' },
                  { title: 'Aprovado', cell: (r) => brl(r.total), alignment: 'fim' },
                ] satisfies Column<Origin>[]
              }
              items={data.origins}
              rowKey={(r) => r.origin}
              label="Cotações aprovadas por origem da peça"
              empty="Nenhuma cotação aprovada."
            />
            <Table
              columns={
                [
                  { title: 'Fornecedor', cell: providerLabel, alignment: 'inicio' },
                  { title: 'Itens', cell: (r) => r.items, alignment: 'fim' },
                  { title: 'Aprovado', cell: (r) => brl(r.total), alignment: 'fim' },
                ] satisfies Column<Provider>[]
              }
              items={data.providers}
              rowKey={(r) => r.name ?? ''}
              label="Cotações aprovadas por fornecedor"
              empty="Nenhuma cotação aprovada."
            />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <dl className="grid grid-cols-3 gap-3 text-xs text-texto-suave">
              <Figure label="Aprovado" value={brl(data.approvedTotal)} />
              <Figure label="Pela menor cotação" value={brl(data.lowestTotal)} />
              <Figure
                label="Acima da menor"
                value={brl(data.aboveLowest)}
                className={above > 0 ? 'text-alerta' : undefined}
              />
            </dl>
            <p className="text-xs text-texto-suave">
              {count(data.atLowest, 'item foi aprovado', 'itens foram aprovados')} pela
              menor cotação, de{' '}
              {count(data.approvedItems, 'item aprovado', 'itens aprovados')} (
              {share(data.atLowest, data.approvedItems)}%).
            </p>
            <Bars
              title="Origem da peça"
              rows={data.origins.map((o) => ({
                key: o.origin,
                label: originLabel(o.origin),
                items: o.items,
                total: o.total,
              }))}
            />
            <Bars
              title="Fornecedores"
              rows={providers.map((p) => ({
                key: p.name ?? '',
                label: providerLabel(p),
                items: p.items,
                total: p.total,
              }))}
            />
          </div>
        ))}
    </PanelCard>
  );
}

function Figure({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt>{label}</dt>
      <dd className={cn('text-base font-semibold tabular-nums text-texto', className)}>
        {value}
      </dd>
    </div>
  );
}

/** Uma lista de barras pelo valor aprovado, com o número de itens ao lado. */
function Bars({
  title,
  rows,
}: {
  title: string;
  rows: { key: string; label: string; items: number; total: string }[];
}) {
  const max = Math.max(1, ...rows.map((r) => money(r.total)));
  return (
    <section className="flex flex-col gap-1.5">
      <h3 className="rotulo">{title}</h3>
      <ol className="flex flex-col gap-1.5">
        {rows.map((r) => (
          <li
            key={r.key}
            className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)_minmax(0,8.5rem)] items-center gap-3 text-xs"
          >
            <span className="truncate text-texto" title={r.label}>
              {r.label}
            </span>
            <span className="h-2 overflow-hidden rounded-pilula bg-superficie-suave">
              <span
                className="block h-full rounded-pilula bg-fase-azul"
                style={{ width: `${(money(r.total) / max) * 100}%` }}
              />
            </span>
            <span className="text-right tabular-nums text-texto-suave">
              <b className="font-semibold text-texto">{brl(r.total)}</b> ·{' '}
              {count(r.items, 'item', 'itens')}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}
